import React from "react";
import { Dialog } from "../../ui/Dialog/Dialog";
import { Badge } from "../../ui/Badge/Badge";
import { Button } from "../../ui/Button/Button";
import {
  Package,
  FileText,
  Calendar,
  AlertTriangle,
  Tag,
  Code,
  Layers,
} from "lucide-react";

/**
 * ServiceDetailsModal Component
 * Displays comprehensive service information in a modal
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @param {Object} props.service - Service object with full details
 */
export const ServiceDetailsModal = ({ open, onClose, service }) => {
  if (!service) return null;

  const {
    serviceId,
    name,
    category,
    serviceType,
    version,
    description,
    userAssistance,
    documentationUrl,
    deprecated,
    endpoints = [],
    releaseDate,
    created_at,
  } = service;

  // Use description or userAssistance as-is (plain text)
  const displayDescription = description || userAssistance;

  // Use existing documentationUrl from backend
  const docUrl = documentationUrl;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      headerText={name || serviceId}
      className="max-w-3xl"
    >
      <div className="space-y-6">
        {/* Deprecation Warning */}
        {deprecated && (
          <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-red-700">
              This service is deprecated
            </span>
          </div>
        )}

        {/* Service ID */}
        <div className="flex items-center gap-3">
          <Package className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <div>
            <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Service ID
            </span>
            <p className="text-sm text-gray-900 font-mono mt-0.5">
              {serviceId}
            </p>
          </div>
        </div>

        {/* Category */}
        {category && (
          <div className="flex items-center gap-3">
            <Tag className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </span>
              <p className="text-sm text-gray-900 mt-0.5">{category}</p>
            </div>
          </div>
        )}

        {/* Service Type */}
        {serviceType && (
          <div className="flex items-center gap-3">
            <Layers className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </span>
              <p className="text-sm text-gray-900 mt-0.5">{serviceType}</p>
            </div>
          </div>
        )}

        {/* Version */}
        {version && (
          <div className="flex items-center gap-3">
            <Calendar className="h-5 w-5 text-blue-600 flex-shrink-0" />
            <div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                Version
              </span>
              <p className="text-sm text-gray-900 mt-0.5">{version}</p>
            </div>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-gray-200"></div>

        {/* Description */}
        {displayDescription && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                Description
              </h3>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {displayDescription}
              </p>
              {docUrl && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <a
                    href={docUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm underline inline-flex items-center gap-1"
                  >
                    View Documentation
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

        {/* API Endpoints */}
        {endpoints && endpoints.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Code className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                API Endpoints
              </h3>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <ul className="space-y-2">
                {endpoints.map((endpoint, index) => (
                  <li key={index} className="text-sm text-gray-700 font-mono">
                    {endpoint}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Release Date */}
        {(releaseDate || created_at) && (
          <div className="text-xs text-gray-500 pt-4 border-t border-gray-200">
            {releaseDate &&
              `Released: ${new Date(releaseDate).toLocaleDateString()}`}
            {!releaseDate &&
              created_at &&
              `Added: ${new Date(created_at).toLocaleDateString()}`}
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <Button design="Transparent" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default ServiceDetailsModal;
